import React, {useCallback} from "react";

import {useGlobalEvent} from "~/contexts/event/GlobalEventBusContext";
import {useUserInput} from "~/contexts/user-input/UserInputCoordinatorProvider";

/**
 * A custom hook that registers physical user inputs (mouse, keyboard) and maps
 * them to indexed player action channels via the UserInputCoordinator.
 */
export function usePlayerActions() {
  const {setTabbing, resetSimulation} = useUserInput();

  // --- Pointer Events (Still controls only the primary player, index 0) ---
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      setTabbing(0, true);
    },
    [setTabbing],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      setTabbing(0, false);
    },
    [setTabbing],
  );

  // --- Keyboard Events (Expanded for three players) ---
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.repeat) return;

      switch (e.key) {
        // General Actions
        case "r":
          resetSimulation();
          break;

        // --- Indexed Player Actions ---
        case " ": // Space bar still controls the primary player
        case "1": // Key "1" also controls primary player
          setTabbing(0, true);
          e.preventDefault();
          break;

        case "2": // Key "2" controls the second player
          setTabbing(1, true);
          e.preventDefault();
          break;

        case "3": // Key "3" controls the third player
          setTabbing(2, true);
          e.preventDefault();
          break;
      }
    },
    [setTabbing, resetSimulation],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case " ":
        case "1":
          setTabbing(0, false);
          e.preventDefault();
          break;

        case "2":
          setTabbing(1, false);
          e.preventDefault();
          break;

        case "3":
          setTabbing(2, false);
          e.preventDefault();
          break;
      }
    },
    [setTabbing],
  );

  // Register all callbacks
  useGlobalEvent("pointerdown", handlePointerDown);
  useGlobalEvent("pointerup", handlePointerUp);
  useGlobalEvent("keydown", handleKeyDown);
  useGlobalEvent("keyup", handleKeyUp);
}
